import {
  IneffectiveAggregates,
  Match,
  Player,
  StaffMember,
  Team,
} from "./types";
import { MatchEvent } from "../../store/useMatchLogStore";
import { formatPlayerName } from "../../lib/nameFormat";
import { parseEventTimestampMs } from "../../lib/eventTime";

export type IneffectiveAction =
  | "Goal"
  | "GoalKick"
  | "FreeKick"
  | "ThrowIn"
  | "Corner"
  | "VAR"
  | "Injury"
  | "OutOfBounds"
  | "Other";

export type IneffectiveTeamKey = "home" | "away" | "neutral";

export interface IneffectiveTotals {
  home: number;
  away: number;
  neutral: number;
  byAction: Record<IneffectiveAction, Record<IneffectiveTeamKey, number>>;
}

export interface IneffectiveBreakdown {
  totals: IneffectiveTotals;
  timeout: {
    totalSeconds: number;
    active: { startMs: number } | null;
  };
  active?: {
    teamKey: IneffectiveTeamKey;
    action: IneffectiveAction;
    startMs: number;
  } | null;
  varActive?: {
    startMs: number;
  } | null;
}

const INEFFECTIVE_ACTIONS: IneffectiveAction[] = [
  "Goal",
  "GoalKick",
  "FreeKick",
  "ThrowIn",
  "Corner",
  "VAR",
  "Injury",
  "OutOfBounds",
  "Other",
];

const normalizeIneffectiveAction = (raw?: string | null): IneffectiveAction => {
  const normalized = String(raw || "")
    .toLowerCase()
    .replace(/[^a-z]+/g, "");
  if (!normalized) return "Other";
  // Injury and OutOfBounds must be explicit branches BEFORE the catch-all.
  if (normalized.includes("injury")) return "Injury";
  // "out" prefix covers "OutOfBounds", "outofbounds", "out*" variants.
  if (normalized.includes("outofbounds") || normalized.startsWith("out"))
    return "OutOfBounds";
  // GoalKick must be checked before "goal" to avoid false match.
  if (
    normalized.includes("goalkick") ||
    normalized.includes("goalki") ||
    normalized === "goalkick"
  )
    return "GoalKick";
  if (normalized.includes("goal")) return "Goal";
  // Foul → FreeKick (Tiros libres).  Also maps old "Foul" and any freekick variant.
  if (
    normalized.includes("foul") ||
    normalized.includes("falta") ||
    normalized.includes("freekick") ||
    normalized === "frekick"
  )
    return "FreeKick";
  // ThrowIn (Laterales).
  if (
    normalized.includes("throwin") ||
    normalized.includes("throw") ||
    normalized === "throwin"
  )
    return "ThrowIn";
  if (normalized.includes("corner")) return "Corner";
  if (normalized.includes("var")) return "VAR";
  // Backward-compat: Card, Substitution, Offside, Referee, and any
  // other legacy value → Other so nothing crashes.
  return "Other";
};

/**
 * Parse an ISO timestamp as UTC even if the string lacks a timezone suffix.
 * Servers may return timezone-naive ISO strings (e.g. "2026-03-13T16:34:02.772000")
 * which browsers parse as local time; appending "Z" forces UTC interpretation.
 */
/**
 * Re-exported from `lib/eventTime` so this module's long-standing import site
 * keeps working. See that module for why a naive server timestamp must be read
 * as UTC and why every comparison has to agree on it.
 */
export const parseTimestampAsUtcMs = parseEventTimestampMs;

const parseClockToMs = (clock?: string | null): number | null => {
  if (!clock) return null;
  const match = String(clock)
    .trim()
    .match(/^(\d+):(\d{2})(?:\.(\d{1,3}))?$/);
  if (!match) return null;
  const minutes = Number(match[1]);
  const seconds = Number(match[2]);
  const millis = Number((match[3] || "0").padEnd(3, "0"));
  if (
    !Number.isFinite(minutes) ||
    !Number.isFinite(seconds) ||
    !Number.isFinite(millis)
  ) {
    return null;
  }
  return (minutes * 60 + seconds) * 1000 + millis;
};

const buildEmptyTotals = (): IneffectiveTotals => {
  const byAction = INEFFECTIVE_ACTIONS.reduce(
    (acc, action) => {
      acc[action] = { home: 0, away: 0, neutral: 0 };
      return acc;
    },
    {} as Record<IneffectiveAction, Record<IneffectiveTeamKey, number>>,
  );
  return { home: 0, away: 0, neutral: 0, byAction };
};

const resolveTeamKey = (
  event: MatchEvent,
  homeTeamIds: string | string[],
  awayTeamIds: string | string[],
  action: IneffectiveAction,
): IneffectiveTeamKey => {
  if (action === "VAR") return "neutral";
  const triggerTeamRaw =
    event.data?.trigger_team_id || event.team_id || "NEUTRAL";
  const triggerTeam = String(triggerTeamRaw || "").toLowerCase();
  if (triggerTeam === "neutral") return "neutral";
  if (triggerTeam === "home") return "home";
  if (triggerTeam === "away") return "away";
  const homeSet = new Set(
    (Array.isArray(homeTeamIds) ? homeTeamIds : [homeTeamIds])
      .map((value) => String(value || "").toLowerCase())
      .filter(Boolean),
  );
  const awaySet = new Set(
    (Array.isArray(awayTeamIds) ? awayTeamIds : [awayTeamIds])
      .map((value) => String(value || "").toLowerCase())
      .filter(Boolean),
  );
  if (homeSet.has(triggerTeam)) return "home";
  if (awaySet.has(triggerTeam)) return "away";
  return "home";
};

export const computeIneffectiveBreakdown = (
  events: MatchEvent[],
  homeTeamIds: string | string[],
  awayTeamIds: string | string[],
  nowMs: number = Date.now(),
): IneffectiveBreakdown => {
  const totals = buildEmptyTotals();
  const stoppages = events
    .map((event, index) => ({ event, index }))
    .filter(
      ({ event }) =>
        event.type === "GameStoppage" &&
        typeof event.data?.stoppage_type === "string",
    )
    .slice()
    .sort((left, right) => {
      // MUST use the same instant interpretation as the per-event
      // `timestampMs` below. Raw `new Date(...)` reads a naive (no-`Z`)
      // server timestamp as LOCAL time, so in UTC-6 an acked TimeoutStart
      // sorted ~6h AFTER an optimistic TimeoutStop: the Stop was replayed
      // first (a no-op with no open timeout), the Start then re-opened one,
      // and `timeout.active` never cleared — the operator's "End T.Off"
      // button appeared dead. Same corruption applied to ClockStop/ClockStart
      // and VARStart/VARStop pairing.
      const leftTs = parseTimestampAsUtcMs(left.event.timestamp);
      const rightTs = parseTimestampAsUtcMs(right.event.timestamp);
      const leftValid = Number.isFinite(leftTs);
      const rightValid = Number.isFinite(rightTs);
      if (leftValid && rightValid && leftTs !== rightTs) {
        return leftTs - rightTs;
      }
      return left.index - right.index;
    })
    .map(({ event }) => event);

  let activeTeamKey: IneffectiveTeamKey | null = null;
  let activeAction: IneffectiveAction | null = null;
  let activeStartMs: number | null = null;
  let pausedByVarTeamKey: IneffectiveTeamKey | null = null;
  let pausedByVarAction: IneffectiveAction | null = null;
  let pausedByTimeoutTeamKey: IneffectiveTeamKey | null = null;
  let pausedByTimeoutAction: IneffectiveAction | null = null;
  let varStartMs: number | null = null;
  let timeoutStartMs: number | null = null;
  let timeoutTotalSeconds = 0;

  const addDuration = (
    teamKey: IneffectiveTeamKey,
    action: IneffectiveAction,
    durationSeconds: number,
  ) => {
    if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return;
    totals[teamKey] += durationSeconds;
    totals.byAction[action][teamKey] += durationSeconds;
  };

  stoppages.forEach((event) => {
    const stoppageType = String(event.data?.stoppage_type || "");
    const timestampMs = (() => {
      const parsedTimestamp = parseTimestampAsUtcMs(event.timestamp);
      if (Number.isFinite(parsedTimestamp)) return parsedTimestamp;
      const fromClock = parseClockToMs(event.match_clock);
      if (fromClock !== null) return fromClock;
      return NaN;
    })();
    if (!Number.isFinite(timestampMs)) return;

    if (stoppageType === "VARStart") {
      if (activeTeamKey && activeAction && activeStartMs !== null) {
        addDuration(
          activeTeamKey,
          activeAction,
          (timestampMs - activeStartMs) / 1000,
        );
        pausedByVarTeamKey = activeTeamKey;
        pausedByVarAction = activeAction;
        activeTeamKey = null;
        activeAction = null;
        activeStartMs = null;
      }
      varStartMs = timestampMs;
      return;
    }
    if (stoppageType === "VARStop") {
      if (varStartMs !== null) {
        addDuration("neutral", "VAR", (timestampMs - varStartMs) / 1000);
      }
      varStartMs = null;
      if (!activeTeamKey && pausedByVarTeamKey && pausedByVarAction) {
        activeTeamKey = pausedByVarTeamKey;
        activeAction = pausedByVarAction;
        activeStartMs = timestampMs;
      }
      pausedByVarTeamKey = null;
      pausedByVarAction = null;
      return;
    }

    if (stoppageType === "TimeoutStart") {
      if (activeTeamKey && activeAction && activeStartMs !== null) {
        addDuration(
          activeTeamKey,
          activeAction,
          (timestampMs - activeStartMs) / 1000,
        );
        pausedByTimeoutTeamKey = activeTeamKey;
        pausedByTimeoutAction = activeAction;
        activeTeamKey = null;
        activeAction = null;
        activeStartMs = null;
      }
      timeoutStartMs = timestampMs;
      return;
    }

    if (stoppageType === "TimeoutStop") {
      if (timeoutStartMs !== null) {
        const timeoutDeltaSeconds = Math.max(
          0,
          (timestampMs - timeoutStartMs) / 1000,
        );
        timeoutTotalSeconds += timeoutDeltaSeconds;
        addDuration("neutral", "Other", timeoutDeltaSeconds);
      }
      timeoutStartMs = null;
      if (!activeTeamKey && pausedByTimeoutTeamKey && pausedByTimeoutAction) {
        activeTeamKey = pausedByTimeoutTeamKey;
        activeAction = pausedByTimeoutAction;
        activeStartMs = timestampMs;
      }
      pausedByTimeoutTeamKey = null;
      pausedByTimeoutAction = null;
      return;
    }

    const action = normalizeIneffectiveAction(
      event.data?.trigger_action || event.data?.reason,
    );
    const teamKey = resolveTeamKey(event, homeTeamIds, awayTeamIds, action);

    if (stoppageType === "ClockStop") {
      activeTeamKey = teamKey;
      activeAction = action;
      activeStartMs = timestampMs;
      pausedByVarTeamKey = null;
      pausedByVarAction = null;
      return;
    }
    if (stoppageType === "ClockStart") {
      if (activeTeamKey && activeAction && activeStartMs !== null) {
        addDuration(
          activeTeamKey,
          activeAction,
          (timestampMs - activeStartMs) / 1000,
        );
      }
      activeTeamKey = null;
      activeAction = null;
      activeStartMs = null;
      pausedByVarTeamKey = null;
      pausedByVarAction = null;
    }
  });

  if (activeTeamKey && activeAction && activeStartMs !== null) {
    const activeDelta = (nowMs - activeStartMs) / 1000;
    addDuration(activeTeamKey, activeAction, activeDelta);
  }

  if (varStartMs !== null) {
    addDuration("neutral", "VAR", (nowMs - varStartMs) / 1000);
  }

  if (timeoutStartMs !== null) {
    const timeoutDeltaSeconds = Math.max(0, (nowMs - timeoutStartMs) / 1000);
    timeoutTotalSeconds += timeoutDeltaSeconds;
    addDuration("neutral", "Other", timeoutDeltaSeconds);
  }

  const active =
    activeTeamKey && activeAction && activeStartMs !== null
      ? {
          teamKey: activeTeamKey,
          action: activeAction,
          startMs: activeStartMs,
        }
      : null;

  const varActive =
    varStartMs !== null
      ? {
          startMs: varStartMs,
        }
      : null;

  const timeoutActive =
    timeoutStartMs !== null
      ? {
          startMs: timeoutStartMs,
        }
      : null;

  return {
    totals,
    timeout: {
      totalSeconds: timeoutTotalSeconds,
      active: timeoutActive,
    },
    active,
    varActive,
  };
};

export const buildIneffectiveBreakdownFromAggregates = (
  aggregates: IneffectiveAggregates,
  nowMs: number = Date.now(),
): IneffectiveBreakdown => {
  const totals = buildEmptyTotals();

  // Iterate all keys the backend sends — normalize each to the new FE taxonomy
  // so both old backend payloads (OutOfBounds, Card, Foul, …) and new ones
  // (GoalKick, FreeKick, ThrowIn, Corner) are handled correctly.
  Object.entries(aggregates.by_action ?? {}).forEach(
    ([rawAction, actionTotals]) => {
      if (!actionTotals) return;
      const action = normalizeIneffectiveAction(rawAction);
      totals.byAction[action].home += actionTotals.home || 0;
      totals.byAction[action].away += actionTotals.away || 0;
      totals.byAction[action].neutral += actionTotals.neutral || 0;
    },
  );

  totals.home = aggregates.totals?.home || 0;
  totals.away = aggregates.totals?.away || 0;
  totals.neutral = aggregates.totals?.neutral || 0;

  let active: {
    teamKey: IneffectiveTeamKey;
    action: IneffectiveAction;
    startMs: number;
  } | null = null;
  let varActive: { startMs: number } | null = null;
  if (aggregates.active?.start_timestamp) {
    const startMs = parseTimestampAsUtcMs(aggregates.active.start_timestamp);
    if (Number.isFinite(startMs)) {
      active = {
        teamKey: aggregates.active.team_key,
        // Normalize the backend action key to the current FE taxonomy so that
        // old backend payloads (e.g. "Foul", "OutOfBounds") land in the correct
        // new category ("FreeKick", "Other", …).
        action: normalizeIneffectiveAction(aggregates.active.action),
        startMs,
      };
      let deltaSeconds = Math.max(0, (nowMs - startMs) / 1000);
      if (aggregates.var_active?.start_timestamp) {
        const varStartMs = parseTimestampAsUtcMs(
          aggregates.var_active.start_timestamp,
        );
        if (Number.isFinite(varStartMs)) {
          const overlapSeconds = Math.max(
            0,
            (nowMs - Math.max(startMs, varStartMs)) / 1000,
          );
          deltaSeconds = Math.max(0, deltaSeconds - overlapSeconds);
        }
      }
      totals[active.teamKey] += deltaSeconds;
      if (totals.byAction[active.action]) {
        totals.byAction[active.action][active.teamKey] += deltaSeconds;
      }
    }
  }

  if (aggregates.var_active?.start_timestamp) {
    const startMs = parseTimestampAsUtcMs(
      aggregates.var_active.start_timestamp,
    );
    if (Number.isFinite(startMs)) {
      varActive = { startMs };
      const deltaSeconds = Math.max(0, (nowMs - startMs) / 1000);
      totals.neutral += deltaSeconds;
      totals.byAction.VAR.neutral += deltaSeconds;
    }
  }

  let timeoutActive: { startMs: number } | null = null;
  let timeoutTotalSeconds = aggregates.timeout?.total_seconds || 0;
  if (aggregates.timeout?.active?.start_timestamp) {
    const startMs = parseTimestampAsUtcMs(
      aggregates.timeout.active.start_timestamp,
    );
    if (Number.isFinite(startMs)) {
      timeoutActive = { startMs };
      timeoutTotalSeconds += Math.max(0, (nowMs - startMs) / 1000);
    }
  }

  return {
    totals,
    timeout: {
      totalSeconds: timeoutTotalSeconds,
      active: timeoutActive,
    },
    active,
    varActive,
  };
};

const SHORT_NAME_STOP_WORDS = new Set([
  "de",
  "del",
  "la",
  "las",
  "los",
  "el",
  "y",
  "the",
  "of",
  "and",
]);

const sanitizeShortName = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.toUpperCase();
};

const resolveConfiguredTeamShortName = (team: any): string | null => {
  const directCandidates = [
    team?.short_name,
    team?.shortName,
    team?.shortname,
    team?.abbreviation,
    team?.abbr,
    team?.acronym,
    team?.initials,
    team?.sigla,
    team?.siglas,
    team?.team_short_name,
  ];

  for (const candidate of directCandidates) {
    const sanitized = sanitizeShortName(candidate);
    if (sanitized) return sanitized;
  }

  const nestedCandidates = [
    team?.team?.short_name,
    team?.team?.shortName,
    team?.team?.shortname,
    team?.team?.abbreviation,
    team?.team?.abbr,
    team?.team?.sigla,
    team?.team?.siglas,
    team?.details?.short_name,
    team?.details?.shortName,
    team?.details?.shortname,
    team?.details?.abbreviation,
    team?.details?.abbr,
    team?.details?.sigla,
    team?.details?.siglas,
    team?.metadata?.short_name,
    team?.metadata?.shortName,
    team?.metadata?.shortname,
    team?.metadata?.abbreviation,
    team?.metadata?.abbr,
    team?.metadata?.sigla,
    team?.metadata?.siglas,
    team?.meta?.short_name,
    team?.meta?.shortName,
    team?.meta?.shortname,
    team?.meta?.abbreviation,
    team?.meta?.abbr,
    team?.meta?.sigla,
    team?.meta?.siglas,
  ];

  for (const candidate of nestedCandidates) {
    const sanitized = sanitizeShortName(candidate);
    if (sanitized) return sanitized;
  }

  return null;
};

export const deriveShortName = (name?: string, fallback: string = "TEAM") => {
  if (!name || typeof name !== "string") return fallback;
  const tokens =
    name
      .trim()
      .match(/[0-9A-Za-zÀ-ÖØ-öø-ÿ]+/g)
      ?.filter(Boolean) ?? [];

  if (!tokens.length) return fallback;

  const meaningful = tokens.filter(
    (token) => !SHORT_NAME_STOP_WORDS.has(token.toLowerCase()),
  );
  const source = meaningful.length > 0 ? meaningful : tokens;
  const initials = source
    .map((token) => token[0])
    .join("")
    .toUpperCase();

  if (initials.length >= 2) return initials.slice(0, 3);

  // Single-word fallback keeps previous behavior for names like "Herediano".
  return tokens[0].slice(0, 3).toUpperCase() || fallback;
};

const normalizeBirthDateValue = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (typeof value === "object" && "$date" in (value as Record<string, any>)) {
    const iso = (value as Record<string, any>).$date;
    return typeof iso === "string" ? iso : null;
  }
  return null;
};

const coercePlayers = (team: any, teamId: string): Player[] => {
  const source = Array.isArray(team?.players)
    ? team.players
    : Array.isArray(team?.lineup)
      ? team.lineup
      : [];

  const rosterSize = source.length;

  return source.map((player: any, index: number) => {
    const defaultStarter = rosterSize >= 11 ? index < 11 : true;
    const isStarter =
      player?.is_starter ??
      player?.isStarter ??
      player?.starter ??
      player?.is_on_field ??
      defaultStarter;

    return {
      id: player?.id ?? player?.player_id ?? `${teamId}-${index + 1}`,
      full_name: formatPlayerName(
        player?.full_name ?? player?.player_name ?? `Player ${index + 1}`,
      ),
      short_name: player?.short_name ?? player?.shortName,
      jersey_number: player?.jersey_number ?? index + 1,
      position: player?.position ?? "MF",
      birth_date: normalizeBirthDateValue(
        player?.birth_date ??
          player?.birthDate ??
          player?.date_of_birth ??
          player?.dateOfBirth ??
          null,
      ),
      is_starter: Boolean(isStarter),
      photo_url: player?.photo_url ?? null,
      photo_image: player?.photo_image ?? null,
    };
  });
};

const coerceStaff = (team: any): StaffMember[] => {
  const source = Array.isArray(team?.technical_staff)
    ? team.technical_staff
    : [];

  return source
    .filter((member: any) => Boolean(member?.staff_id))
    .map((member: any) => ({
      staff_id: member.staff_id,
      name: member?.name ?? "",
      role: String(member?.role ?? ""),
    }));
};

export const normalizeTeamFromApi = (
  team: any,
  fallbackLabel: string,
): Team => {
  const teamId = team?.team_id ?? team?.id ?? fallbackLabel;
  const name = team?.name ?? fallbackLabel;
  const shortName =
    resolveConfiguredTeamShortName(team) ??
    deriveShortName(name, fallbackLabel);

  return {
    id: teamId,
    team_id: team?.team_id ?? teamId,
    name,
    short_name: shortName,
    logo_url: team?.logo_url ?? null,
    logo_image: team?.logo_image ?? null,
    players: coercePlayers(team, teamId),
    technical_staff: coerceStaff(team),
    primary_color: team?.primary_color ?? null,
    secondary_color: team?.secondary_color ?? null,
    kit: team?.kit ?? null,
  };
};

export const normalizeMatchPayload = (payload: any): Match => {
  if (!payload) {
    throw new Error("Missing match payload");
  }

  const id = payload.id ?? payload.match_id ?? payload._id;
  if (!id) {
    throw new Error("Match payload missing ID");
  }

  return {
    id,
    match_time_seconds: payload.match_time_seconds ?? 0,
    status: payload.status ?? "Live",
    current_period_start_timestamp: payload.current_period_start_timestamp,
    clock_seconds_at_period_start: payload.clock_seconds_at_period_start,
    period_timestamps: payload.period_timestamps ?? {},
    ineffective_time_seconds: payload.ineffective_time_seconds ?? 0,
    ineffective_aggregates: payload.ineffective_aggregates ?? null,
    clock_mode:
      payload.clock_mode === "INEFFECTIVE" ? "INEFFECTIVE" : "EFFECTIVE",
    last_mode_change_timestamp: payload.last_mode_change_timestamp,
    home_team: normalizeTeamFromApi(payload.home_team, "HOME"),
    away_team: normalizeTeamFromApi(payload.away_team, "AWAY"),
    share_token: payload.share_token,
  };
};

export const formatMatchClock = (seconds?: number): string => {
  const rawSeconds = seconds ?? 0;
  const safeSeconds = Number.isFinite(rawSeconds) ? Math.max(0, rawSeconds) : 0;
  const minutes = Math.floor(safeSeconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = Math.floor(safeSeconds % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${secs}.000`;
};

export const normalizeMatchClock = (input: string): string | null => {
  if (!input) return null;
  // Already valid?
  if (/^\d{1,3}:\d{2}(\.\d{1,3})?$/.test(input)) return input;

  // Try to parse basic formats
  // MM:SS
  const parts = input.split(":");
  if (parts.length === 2) {
    const mm = parseInt(parts[0], 10);
    const ss = parseFloat(parts[1]);
    if (!isNaN(mm) && !isNaN(ss)) {
      return formatMatchClock(mm * 60 + ss);
    }
  }

  // Pure numbers: treat as minutes if <= 150, else maybe milliseconds or MMSS?
  // Let's keep it simple: if it looks invalid, return null to signal fallback.
  return null;
};

/* ── Timer-formula helpers (pure, testable) ──────────────────── */

export interface TimerFormulaInput {
  /** Pure ball-in-play seconds from useMatchTimer (frozen during INEFFECTIVE mode). */
  effectiveTime: number;
  /** Global accumulated ineffective seconds. */
  ineffectiveSeconds: number;
  /** Timeout seconds. */
  timeoutSeconds: number;
  /** VAR seconds (displayed separately, excluded from global clock). */
  varTimeSeconds: number;
  /** Team-attributed ineffective totals (excluding VAR & Injury). */
  teamIneffective: { home: number; away: number };
  /**
   * Home/away possession share (e.g. from `computePossessionSplit`, contested
   * excluded) used to split `effectiveTime` per team. When omitted, or when
   * home+away is 0 (no possession data yet), falls back to the old
   * shared-effective behavior (both teams show the full global effective).
   */
  possession?: { home: number; away: number };
}

export interface TimerFormulaResult {
  globalSeconds: number;
  totalEffectiveTime: number;
  totalIneffectiveSeconds: number;
  /**
   * Passed through from the input, unchanged, for display only ("of the Xs
   * ineffective this half, Ys was timeout") — see the canonical
   * decomposition below for why this is never added into `globalSeconds`.
   */
  timeoutSeconds: number;
  homeEffectiveSeconds: number;
  awayEffectiveSeconds: number;
  homeEffectivePercent: string;
  awayEffectivePercent: string;
  homeIneffectivePercent: string;
  awayIneffectivePercent: string;
}

/**
 * Compute per-team effective/ineffective display values.
 *
 * Canonical decomposition (PMA-COCK-131, docs/spec-driven/cockpit-contract.md):
 *
 *   global = effective + ineffective          (VAR excluded from this sum
 *                                                entirely — tracked and
 *                                                displayed separately, own
 *                                                clock card, never rolled
 *                                                into global)
 *   ineffective = team_attributed + neutral_referee + timeout
 *
 * Timeout is a labeled SUBDIVISION of `ineffectiveSeconds`, not a peer
 * bucket the way VAR is — `handleTimeoutToggle` (`useCockpitClockHandlers.ts`)
 * switches `clock_mode` to `INEFFECTIVE` for a timeout's entire duration
 * (mirroring `handleModeSwitch`, the same primitive VAR uses), so by the
 * time `ineffectiveSeconds` reaches this function it already contains any
 * timeout time. `timeoutSeconds` is accepted here ONLY to pass through into
 * the result for display ("of the Xs ineffective this half, Ys was
 * timeout") — it must NEVER be added into `globalSeconds`, or any other sum
 * that already contains `ineffectiveSeconds`, or it silently double-counts
 * (the exact defect this canonical decomposition exists to prevent — see
 * PMA-COCK-131 for the full incident and `utils.timerFormulas.test.ts`'s
 * AC3 regression test). If a FUTURE stoppage class needs the same
 * treatment, it is a subdivision of `ineffectiveSeconds` too, not a new
 * summed term — do not repeat this defect for it.
 *
 * Other rules:
 * - effectiveTime is already pure ball-in-play (timeout excluded, per the
 *   decomposition above); it must NOT be reduced by team-attributed
 *   stoppages (those seconds were never included in it).
 * - Per-team effective seconds are split from the global effectiveTime by
 *   possession share (contested possession excluded from the denominator),
 *   so home + away always sum EXACTLY to effectiveTime: home is rounded,
 *   away takes the remainder (not independently rounded) to guarantee the
 *   invariant regardless of rounding. When possession is omitted or its
 *   home+away denominator is 0 (no data yet), both teams fall back to the
 *   full global effectiveTime (the pre-possession-split behavior).
 * - Per-team denominators = that team's effective + that team's attributed
 *   ineffective (both now per-team, not the shared global effective).
 * - Effective % = team's effective / team's denom.
 * - Ineffective % = team's ineffective / team's denom.
 */
export const computeTimerFormulas = (
  input: TimerFormulaInput,
): TimerFormulaResult => {
  const {
    effectiveTime: rawEffectiveTime,
    ineffectiveSeconds,
    timeoutSeconds,
    teamIneffective,
    possession,
  } = input;

  // Guard a negative effectiveTime (should never happen from useMatchTimer,
  // but a negative input has no valid split) before it can propagate into
  // either team's share.
  const effectiveTime = Math.max(0, rawEffectiveTime);

  // global = effective + ineffective only (VAR excluded, unchanged; timeout
  // is NOT added again here — see the canonical decomposition above).
  const globalSeconds = effectiveTime + ineffectiveSeconds;

  const possessionDenom = possession ? possession.home + possession.away : 0;

  let homeEffectiveSeconds: number;
  let awayEffectiveSeconds: number;
  if (possessionDenom > 0) {
    // Clamp the rounded home share into [0, effectiveTime] BEFORE deriving
    // the away remainder: when one team holds ~all possession and the
    // fractional remainder is >= 0.5, Math.round can push the home share
    // above effectiveTime, which would otherwise send the derived away
    // remainder negative (e.g. effectiveTime=2.6, home share 100% ->
    // round(2.6)=3, away = 2.6-3 = -0.4). Clamping home keeps the
    // home+away === effectiveTime invariant intact by construction, since
    // away is still derived as the remainder, never independently rounded.
    const roundedHome = Math.round(
      (effectiveTime * possession!.home) / possessionDenom,
    );
    homeEffectiveSeconds = Math.min(Math.max(roundedHome, 0), effectiveTime);
    awayEffectiveSeconds = effectiveTime - homeEffectiveSeconds;
  } else {
    homeEffectiveSeconds = effectiveTime;
    awayEffectiveSeconds = effectiveTime;
  }

  const homeDenom = homeEffectiveSeconds + teamIneffective.home;
  const awayDenom = awayEffectiveSeconds + teamIneffective.away;

  const homeEffectivePercent =
    homeDenom > 0
      ? `${((homeEffectiveSeconds / homeDenom) * 100).toFixed(1)}%`
      : "0.0%";
  const awayEffectivePercent =
    awayDenom > 0
      ? `${((awayEffectiveSeconds / awayDenom) * 100).toFixed(1)}%`
      : "0.0%";

  const homeIneffectivePercent =
    homeDenom > 0
      ? `${((teamIneffective.home / homeDenom) * 100).toFixed(1)}%`
      : "0.0%";
  const awayIneffectivePercent =
    awayDenom > 0
      ? `${((teamIneffective.away / awayDenom) * 100).toFixed(1)}%`
      : "0.0%";

  return {
    globalSeconds,
    totalEffectiveTime: effectiveTime,
    totalIneffectiveSeconds: ineffectiveSeconds,
    timeoutSeconds,
    homeEffectiveSeconds,
    awayEffectiveSeconds,
    homeEffectivePercent,
    awayEffectivePercent,
    homeIneffectivePercent,
    awayIneffectivePercent,
  };
};

/* ── Possession-split helper (pure, testable) ──────────────────── */

export interface PossessionSplitInput {
  /** Weight attributed to the home team (e.g. completed/attempted passes). */
  homeWeight: number;
  /** Weight attributed to the away team. */
  awayWeight: number;
  /** Weight attributed to neither team (e.g. duels — 50/50 contested balls). */
  contestedWeight: number;
}

export interface PossessionSplitResult {
  homePossession: number;
  awayPossession: number;
  contestedPossession: number;
}

/**
 * Split possession into home / away percentages that always sum to exactly
 * 100 (EPIC-110 D1).
 *
 * - Share = homeWeight / (homeWeight + awayWeight); the contested weight
 *   (duels) no longer influences possession.
 * - Largest-remainder rounding; a tie on the remainder goes to home.
 * - With no home/away weight, falls back to 50/50.
 * - `contestedPossession` is kept for compatibility and is always 0.
 */
export const computePossessionSplit = ({
  homeWeight,
  awayWeight,
}: PossessionSplitInput): PossessionSplitResult => {
  const total = homeWeight + awayWeight;
  if (total <= 0) {
    return { homePossession: 50, awayPossession: 50, contestedPossession: 0 };
  }
  const homeExact = (homeWeight / total) * 100;
  const homeFloor = Math.floor(homeExact);
  const awayExact = (awayWeight / total) * 100;
  const awayFloor = Math.floor(awayExact);
  let homePossession = homeFloor;
  let awayPossession = awayFloor;
  if (homeFloor + awayFloor < 100) {
    const homeFrac = homeExact - homeFloor;
    const awayFrac = awayExact - awayFloor;
    if (homeFrac >= awayFrac) homePossession += 1;
    else awayPossession += 1;
  }
  return { homePossession, awayPossession, contestedPossession: 0 };
};
